
export interface ContactEmail {
    id: number;
    /** The service carries a missing address as an entry with no value, so this is not always set. */
    email: string | null;
}

export interface ContactPhoneNumber {
    id: number;
    country_code: number;
    phone_number: string;
    phone_full: string;
}

export interface ContactTag {
    id?: number;
    name?: string;
    code?: string;
}

export interface ContactDeviceMap {
    id?: number;
    device_id?: string;
    device_name?: string;
    last_seen?: string;
}

export interface MinorEligibility {
    eligible: boolean;
    reason?: string;
    consent_signed?: boolean;
    agreement_variant?: 'standard' | 'bekiares';
}

export interface Contact {
    id: number;
    name: string;
    tags: ContactTag[] | string;
    trusted_person_id: number | null;
    emails: ContactEmail[];
    phone_numbers: ContactPhoneNumber[];
    blocked: boolean;
    public_key: string | null;
    risk: string | null;
    licensed: boolean | null;
    license?: string;
    access: boolean;
    is_minor: boolean;
    device_maps: ContactDeviceMap[];
    // Additional fields for family protection view
    exposures_count?: number;
    removals_count?: number;
    removals_count_30_days_ago?: number;
    in_progress_count?: number;
    in_progress_count_30_days_ago?: number;
    last_scan?: string | null;
    is_self?: boolean;
}

/**
 * The body PUT /people/{id}/contacts/{contact} takes. It is a REPLACE, not a patch: the
 * emails and phone numbers sent become the whole list (an empty list clears it), and a key
 * left out leaves that relation as it was (app-api `ContactHelper::update`). `name` is required
 * on every call.
 * Emails travel as plain strings; a phone number as the two parts the endpoint stores.
 */
export interface ContactUpdatePayload {
    name: string;
    emails: string[];
    phone_numbers: Array<{ country_code: number; phone_number: string }>;
}

/** The same body, plus the two fields only a create sets. */
export interface ContactCreatePayload extends ContactUpdatePayload {
    tags?: string[];
    blocked?: boolean;
}

/**
 * What a create attempt is known to have achieved. `created` is the POST: it is the fact the
 * form acts on, because a member that exists must never leave a dialog open on a duplicate it
 * now conflicts with. `contact` is the row, which only the list refresh can supply — null there
 * means created but unresolved, and a caller that wanted the row says so in its own words.
 */
export interface ContactCreateResult {
    created: boolean;
    contact: Contact | null;
}

export interface ContactSelect {
    id: number;
    name: string;
    type: ContactType;
}

export enum ContactType {
    PERSON = 'person',
    CONTACT = 'contact'
}
